
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

export const usePatientDeletion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { session } = useAuth();

  const deleteMedication = useMutation({
    mutationFn: async (medicationId: string) => {
      const { error } = await supabase
        .from("medications")
        .delete()
        .eq("id", medicationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-medications"] });
      toast({
        title: "Médicament supprimé",
        description: "Le médicament a été supprimé avec succès",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le médicament",
      });
    },
  });

  const deleteJournalEntry = useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await supabase
        .from("health_journal")
        .delete()
        .eq("id", entryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-health-journal"] });
      toast({
        title: "Entrée supprimée",
        description: "L'entrée du journal a été supprimée avec succès",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer l'entrée du journal",
      });
    },
  });

  const deleteAppointment = useMutation({
    mutationFn: async (appointmentId: string) => {
      const { error } = await supabase
        .from("appointments")
        .delete()
        .eq("id", appointmentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-appointments"] });
      toast({
        title: "Rendez-vous supprimé",
        description: "Le rendez-vous a été supprimé avec succès",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le rendez-vous",
      });
    },
  });

  const deletePatientFile = useMutation({
    mutationFn: async (fileId: string) => {
      if (!session?.user?.id) throw new Error("Non authentifié");

      const { error } = await supabase
        .from("patient_files")
        .delete()
        .eq("id", fileId)
        .eq("professional_id", session.user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-files"] });
      queryClient.invalidateQueries({ queryKey: ["professional-patients"] });
      toast({
        title: "Dossier supprimé",
        description: "Le dossier patient a été supprimé avec succès",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le dossier patient",
      });
    },
  });

  return {
    deleteMedication,
    deleteJournalEntry,
    deleteAppointment,
    deletePatientFile,
  };
};
